<template>
  <button
    class="cs-button"
    :class="buttonClasses"
    :style="{
      width: buttonWidth,
      borderRadius: buttonRounded,
    }"
    :disabled="internalDisabled"
    @click="handleClick"
  >
    <slot />
  </button>
</template>

<script>
import { inArray } from '@/lib/component/validators';

export default {
  name: 'Button',
  props: {
    width: {
      type: [String, Number],
      default() {
        return 'auto';
      },
    },
    type: {
      type: String,
      validator: inArray(['primary', 'secondary', 'transparent']),
      default() {
        return 'primary';
      },
    },
    rounded: {
      type: [String, Number],
      default() {
        return '50px';
      },
    },
    size: {
      type: String,
      validator: inArray(['small', 'medium', 'large']),
      default() {
        return 'medium';
      },
    },
    disabled: {
      type: Boolean,
      default() {
        return false;
      },
    },
    active: {
      type: Boolean,
      default() {
        return false;
      },
    },
    loading: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  data() {
    return {
      internalDisabled: false,
    };
  },
  computed: {
    buttonWidth() {
      return typeof this.width === 'string' ? this.width : `${this.width}px`;
    },
    buttonRounded() {
      return typeof this.rounded === 'string' ? this.rounded : `${this.rounded}px`;
    },
    sizeClass() {
      return `size-${this.size}`;
    },
    buttonClasses() {
      return {
        [this.type]: true,
        [this.sizeClass]: true,
        disabled: this.internalDisabled,
        active: this.active,
        loading: this.loading,
      };
    },
  },
  watch: {
    disabled: {
      immediate: true,
      handler(val) {
        this.internalDisabled = val;
      },
    },
  },
  methods: {
    handleClick(e) {
      if (this.internalDisabled || this.loading) {
        return;
      }

      this.$emit('click', e);
    },
  },
};
</script>

<style lang="sass" scoped>
  $white: #fff
  $white-200: #ccc
  $white-300: #9f9f9f
  $white-700: #4f4e5c
  $white-800: #3a3a49
  $green: #5bb100
  $green-100: #7cb342
  $blue-700: #2d2d45
  $blue-800: #27283c
  $blue-900: #1e1d2f
  $violet: #9b51e0
  $obsidian-color: #fbf7ff
  $obsidian-active-background: #fdfbff

  .cs-button
    flex: none

    display: inline-flex
    align-items: center
    justify-content: center

    border: none
    outline: none
    font-weight: 400

    box-sizing: border-box
    cursor: pointer

    &.primary
      color: $white
      background-color: $green-100
      box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.25)

      &:hover,
      &.hover
        box-shadow: inset 0px -4px 0px rgba(0, 0, 0, 0.25)

      &:active,
      &.active
        background-color: $green
        box-shadow: inset 0px 2px 0px rgba(0, 0, 0, 0.25)

      &:disabled,
      &.disabled
        background-color: $white-200
        box-shadow: none
        opacity: 0.5
        cursor: default

      &.loading
        color: $blue-900
        background-color: $blue-900
        opacity: 0.25
        cursor: wait

    &.secondary
      color: $violet
      background-color: rgba(155, 81, 224, 0.2)

      &:hover,
      &.hover
        background-color: rgba(155, 81, 224, 0.1)
        box-shadow: inset 0px -4px 0px rgba(155, 81, 224, 0.1)

      &:active,
      &.active
        background-color: rgba(155, 81, 224, 0.3)
        box-shadow: inset 0 2px 0 rgba(155, 81, 224, 0.4)

      &:disabled,
      &.disabled
        color: $white-300
        background-color: $white-200
        opacity: 0.5
        cursor: default

        &:hover,
        &.hover
          box-shadow: none

      &.loading
        color: $blue-900
        background-color: $blue-900
        opacity: 0.25
        cursor: wait

    &.transparent
      color: $violet
      background-color: transparent
      box-shadow: inset 0 0 0 1px $violet
      border: 1px solid transparent

      &:hover,
      &.hover
        background-color: rgba(155, 81, 224, 0.05)
        box-shadow: inset 0px -4px 0px $violet

      &:active,
      &.active
        box-shadow: inset 0px 2px 0px $violet
        background-color: rgba(155, 81, 224, 0.25)
        border: 1px solid $violet

      &:disabled,
      &.disabled
        color: $white-300
        background-color: transparent
        border: 1px solid rgba(30, 29, 47, .1)
        box-shadow: none
        cursor: default

        &:hover,
        &.hover
          box-shadow: none

      &.loading
        color: transparent
        background-color: rgba(30, 29, 47, 0.2)
        border: 1px solid $blue-900
        box-shadow: none
        opacity: 0.25
        cursor: wait

        &:hover,
        &.hover
          box-shadow: none

    &.size-medium
      width: 340px
      padding: 16px
      font-size: 14px
      line-height: 16px

    &.size-small
      width: 200px
      padding: 12px
      font-size: 14px
      line-height: 16px
</style>
